<template>
    <div class="toolbox" v-if="canToolboxBeShown">
        <button class="button is-rounded is-small is-light" @click="enableEditMode" :title="translate('page.category_manager.rename')">
            <span class="icon">
                <i class="fa fa-edit" />
            </span>
        </button>
        <button
            id="changeSettings"
            class="button is-rounded is-small is-light"
            @click="changeParameterSettings"
            :title="translate('page.category_manager.change_settings')"
        >
            <span class="icon">
                <i class="fa fa-wrench" />
            </span>
        </button>
        <button
            v-if="!hasAnyChildren(node.id)"
            class="button is-rounded is-small is-light"
            @click="extendNode"
            :title="translate('page.category_manager.extend_parameter')"
        >
            <span class="icon">
                <i class="fa fa-expand-arrows-alt" />
            </span>
        </button>
        <LoadingButton
            :is-loading="duplicateInProgress"
            class="button is-rounded is-small is-light"
            @click="duplicateParameter"
            :disabled="isDuplicateActionDisabled"
            :title="translate('page.category_manager.duplicate_category')"
        >
            <span class="icon"> <i class="far fa-copy"></i></span>
        </LoadingButton>
        <button
            class="button is-rounded is-small is-light"
            @click="enableMoveMode"
            :disabled="isMoveModeBlocked"
            :title="translate('page.category_manager.move_node')"
        >
            <span class="icon">
                <i class="fas fa-retweet"></i>
            </span>
        </button>
        <button
            class="button delete-button is-rounded is-small is-danger"
            @click="deleteParameter(node.id)"
            :title="translate('common.delete')"
        >
            <span class="icon">
                <i class="fa fa-trash-alt" />
            </span>
        </button>
    </div>
    <div v-else-if="isMoveModeEnabled" class="toolbox">
        <LoadingButton
            :is-loading="moveInProgress"
            class="button is-rounded is-small is-success"
            :disabled="isConfirmMoveBlocked"
            @click="confirmMove"
        >
            {{ translate("page.category_manager.move_here") }}
        </LoadingButton>
        <button class="button is-rounded is-small is-danger" @click="cancelMove">
            {{ translate("common.cancel") }}
        </button>
    </div>
</template>

<script setup lang="ts">
    import { ref, computed } from "vue";
    import { CHANGE_PARAMETER_SETTINGS, DELETE_PARAMETER_TREE, ENABLE_EDIT_MODE, eventBus } from "./eventBus";
    import { translate } from "../Common/translations";
    import categoriesApi from "../Api/Categories/categories";
    import { handleError } from "../Common/errorHandler";
    import { successToast } from "../Common/toasts";
    import LoadingButton from "../Common/LoadingButton.vue";
    import { useCategoriesManagerStore } from "./categoriesManagerStore";
    import type { CategoryNode } from "../Api/Categories/categories";

    const props = defineProps<{
        node: CategoryNode;
        breadcrumb: number[];
    }>();

    const store = useCategoriesManagerStore();

    const moveInProgress = ref(false);
    const duplicateInProgress = ref(false);

    const hasAnyChildren = computed(() => store.hasAnyChildren);

    const canToolboxBeShown = computed(() => {
        if (store.moveModeEnabled > 0) {
            return false;
        }

        return (
            store.editModeEnabled !== props.node.id && store.addingModeEnabled !== props.node.id && !store.categoriesModificationsEnabled
        );
    });

    const isMoveModeEnabled = computed(() => {
        return store.moveModeEnabled > 0;
    });

    const isConfirmMoveBlocked = computed(() => {
        if (store.moveModeEnabled === props.node.id) {
            return true;
        }

        const indexOfNodeBeingMoved = props.breadcrumb.indexOf(store.moveModeEnabled);
        if (indexOfNodeBeingMoved === -1) {
            return false;
        }

        const breadcrumbFromMovedElement = props.breadcrumb.slice(indexOfNodeBeingMoved);

        return breadcrumbFromMovedElement.indexOf(store.moveModeEnabled) > -1;
    });

    const isMoveModeBlocked = computed(() => {
        return props.node.id === store.rootNode?.id;
    });

    const isDuplicateActionDisabled = computed(() => {
        return props.node.id === store.rootNode?.id;
    });

    function extendNode() {
        store.categoriesModificationsEnabled = true;
        store.extendNode({
            parentId: props.node.id,
        });
        store.extendedNodes[props.node.id] = true;
    }

    function changeParameterSettings() {
        eventBus.$emit(CHANGE_PARAMETER_SETTINGS, props.node.id);
    }

    function enableEditMode() {
        eventBus.$emit(ENABLE_EDIT_MODE, props.node.id);
    }

    function enableMoveMode() {
        store.categoriesModificationsEnabled = true;
        store.moveModeEnabled = props.node.id;
    }

    function deleteParameter(id: number) {
        eventBus.$emit(DELETE_PARAMETER_TREE, id);
    }

    function confirmMove() {
        moveInProgress.value = true;
        return categoriesApi
            .moveParameterUnderNewParent(store.eventId, store.moveModeEnabled, props.node.id)
            .then(() => {
                successToast(translate("page.category_manager.category_moved"));
            })
            .catch(handleError)
            .then(() => {
                return store.refreshCategories();
            })
            .finally(() => {
                moveInProgress.value = false;
                cancelMove();
            });
    }

    function cancelMove() {
        store.categoriesModificationsEnabled = false;
        store.moveModeEnabled = 0;
    }

    function duplicateParameter() {
        duplicateInProgress.value = true;
        return categoriesApi
            .duplicateParameterUnderSameParent(store.eventId, props.node.id)
            .then(() => {
                successToast(translate("page.category_manager.category_duplicated"));
            })
            .catch(handleError)
            .finally(() => {
                duplicateInProgress.value = false;
                return store.refreshCategories();
            });
    }
</script>

<style scoped></style>
